import { gql, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import {
  Button,
  Menu,
  MenuButton,
  MenuContent,
  MenuHeader,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuTitle,
} from '@apollo/orbit';
import classnames from 'classnames';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { useOperationInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { date } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import {
  ErrorInsightsFilterQuery,
  ErrorInsightsFilterQueryVariables,
  ErrorInsightsListFilterInput,
  ErrorInsightsListGroupByColumn,
} from 'src/lib/graphqlTypes/types';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { notEmpty } from 'src/lib/ts/notEmpty';

import { getRouteParamForGroupOption } from './getRouteParam';
import { useDisplayNameTranslator } from './useDisplayNameTranslator';

const errorInsightsFilterFragment = gql`
  fragment ErrorInsightsFilterFragment on ErrorInsightsListItem {
    clientName
    clientVersion
    code
    operationId
    operationName
    path
    serviceOrAgent
  }
`;

export const errorInsightsFilterQuery = gql`
  query ErrorInsightsFilterQuery(
    $graphId: ID!
    $variant: String!
    $from: Timestamp!
    $to: Timestamp!
    $filter: ErrorInsightsListFilterInput
    $groupBy: [ErrorInsightsListGroupByColumn!]
  ) {
    graph(id: $graphId) {
      id
      variant(name: $variant) {
        id
        errorInsightsList(
          from: $from
          to: $to
          filter: $filter
          groupBy: $groupBy
        ) {
          edges {
            node {
              ...ErrorInsightsFilterFragment
            }
            cursor
          }
          pageInfo {
            endCursor
            startCursor
          }
          totalCount
        }
      }
    }
  }
  ${errorInsightsFilterFragment}
`;

interface Props {
  dimension: keyof Pick<
    ErrorInsightsListFilterInput,
    'serviceOrAgent' | 'code'
  >;
}

function groupOptionForDimension(dimension: Props['dimension']) {
  switch (dimension) {
    case 'serviceOrAgent':
      return ErrorInsightsListGroupByColumn.SERVICE_OR_AGENT;
    case 'code':
      return ErrorInsightsListGroupByColumn.CODE;
    default:
      assertUnreachable(dimension);
  }
}

function routeParamForDimension(dimension: Props['dimension']) {
  return getRouteParamForGroupOption(groupOptionForDimension(dimension));
}

function dimDisplayName(dimension: Props['dimension']) {
  if (dimension === 'code') {
    return 'error code';
  } else if (dimension === 'serviceOrAgent') {
    return 'service';
  } else {
    return dimension;
  }
}

export const FilterButton = ({ dimension }: Props) => {
  const { routeParams, setFilterParams, hasActiveFiltersForSubset } =
    useOperationInsightsFilter();
  const trackCustomEvent = useTrackCustomEvent();

  const timeRange = routeParams.range ?? 'lastDay';
  const { timeFrom, timeTo } = date.getDateBounds(
    timeRange,
    {
      from: routeParams.from,
      to: routeParams.to,
    },
    undefined,
    routeParams.fastModeRange,
  );
  const graphRef = useGraphRef();

  const { data } = useQuery<
    ErrorInsightsFilterQuery,
    ErrorInsightsFilterQueryVariables
  >(errorInsightsFilterQuery, {
    variables: {
      graphId: graphRef?.graphId ?? '',
      variant: graphRef?.graphVariant ?? '',
      groupBy: groupOptionForDimension(dimension),
      filter: {
        ...(routeParams.query ? { operationId: routeParams.query } : {}),
      },
      from: timeFrom,
      to: timeTo,
    },
  });

  const { translateToDisplayName } = useDisplayNameTranslator();
  const [hoveredValue, setHoveredValue] = useState<string>();

  const allValues = useMemo(
    () =>
      data?.graph?.variant?.errorInsightsList.edges
        ?.map((e) => e.node?.[dimension] ?? '')
        .filter(notEmpty)
        .sort() ?? [],
    [data?.graph?.variant?.errorInsightsList.edges, dimension],
  );

  const handleFilterChange = useCallback(
    (values: string[]) => {
      const allValuesSelected = values.length === allValues.length;
      const errorsFilter = {
        [routeParamForDimension(dimension)]:
          values.length > 0 && !allValuesSelected ? values : RouteConfig.DELETE,
      };
      setFilterParams(insightsRouteConfig, errorsFilter);
    },
    [allValues.length, dimension, setFilterParams],
  );

  const handleMenuOptionChange = (values: string[]) => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'filter_dropdown_filter_option_click',
    });
    handleFilterChange(values);
  };

  const handleOnlyClick = (value: string) => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'filter_dropdown_filter_only_click',
    });
    handleFilterChange([value]);
  };

  const handleResetClick = () => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'filter_dropdown_reset_click',
    });
    handleFilterChange([]);
  };

  const handleMenuOpen = () => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'filter_dropdown_open',
    });
  };
  const handleMenuClose = () => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'filter_dropdown_close',
    });
  };

  const routeParamKey = routeParamForDimension(dimension);
  const filterFromParams = routeParams[routeParamKey];
  const isFilterActive =
    !!filterFromParams &&
    filterFromParams !== 'all' &&
    hasActiveFiltersForSubset({ [routeParamKey]: undefined });
  const selectedValues =
    !filterFromParams || filterFromParams === 'all' || !isFilterActive
      ? allValues
      : filterFromParams;

  return (
    <Menu
      closeOnSelect={false}
      onOpen={handleMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuButton
        as={Button}
        size="md"
        variant="secondary"
        rightIcon={<IconChevronDown />}
      >
        {!isFilterActive
          ? _.capitalize(dimDisplayName(dimension))
          : `${selectedValues.length} ${format.pluralize(selectedValues.length, dimDisplayName(dimension))}`}
      </MenuButton>
      <MenuContent className="min-w-[300px] max-w-[420px]">
        <MenuHeader className="items-center">
          <MenuTitle>Filter by {dimDisplayName(dimension)}</MenuTitle>
          <Button
            variant="buttonLink"
            size="sm"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleResetClick();
            }}
            className={classnames('mt-1 text-blue', {
              invisible: !isFilterActive,
            })}
          >
            Reset
          </Button>
        </MenuHeader>
        <MenuList>
          <MenuOptionGroup
            type="checkbox"
            value={selectedValues}
            onChange={handleMenuOptionChange}
          >
            {allValues.length === 0 ? (
              <MenuItem isDisabled>
                No{' '}
                {format.pluralize(
                  selectedValues.length,
                  dimDisplayName(dimension),
                )}{' '}
                found
              </MenuItem>
            ) : (
              allValues.map((v) => {
                return (
                  <MenuItemOption
                    type="checkbox"
                    isTruncated
                    key={v}
                    value={v}
                    onMouseEnter={() => setHoveredValue(v)}
                    onMouseLeave={() => setHoveredValue(undefined)}
                    rightIcon={
                      <Button
                        variant="buttonLink"
                        className={classnames(
                          'ml-4',
                          hoveredValue === v ? 'visible' : 'invisible',
                        )}
                        onClick={(evt) => {
                          evt.preventDefault();
                          evt.stopPropagation();
                          handleOnlyClick(v);
                        }}
                      >
                        only
                      </Button>
                    }
                  >
                    {translateToDisplayName(
                      v,
                      groupOptionForDimension(dimension),
                    )}
                  </MenuItemOption>
                );
              })
            )}
          </MenuOptionGroup>
        </MenuList>
      </MenuContent>
    </Menu>
  );
};
